import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PRIORITIES } from "./Priorities";
import AudioRecorder from './AudioRecorder';
import { toast } from 'react-toastify';


const Edit = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({});
    const [imagePaths, setImagePaths] = useState(null);
    const [isEditable, setIsEditable] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioFilename, setAudioFilename] = useState('');
    const [insertedMongoID, setInsertedMongoID] = useState('');
    const [audioPath, setAudioPath] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const backendURL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (!location.state) {
            navigate('/ocr');
        } else {
            const data = { ...location.state.ocrResult };

            // Extract imagepaths
            if (data.imagepaths) {
                setImagePaths(data.imagepaths);
                delete data.imagepaths;
            }

            setFormData(data);
        }
    }, [location, navigate]);

    const handleEditToggle = () => setIsEditable(true);

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleDelete = (keyToDelete) => {
        const updated = { ...formData };
        delete updated[keyToDelete];
        setFormData(updated);
    };

    const handleAddField = () => {
        const trimmedKey = newKey.trim();
        if (!trimmedKey) {
            toast.warn("Any field cannot be empty");
            return;
        }
        if (formData.hasOwnProperty(trimmedKey)) {
            toast.warn("The same field already exists");
            return;
        }

        setFormData(prev => ({ ...prev, [trimmedKey]: newValue }));
        setNewKey('');
        setNewValue('');
    };

    const toCamelCase = (str) => {
        // Remove special characters
        return str
            .toLowerCase()
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .replace(/ (.)/g, (match, group1) => group1.toUpperCase());
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const handleAudioComplete = (blob, filename) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            setAudioBlob(blob);
            setAudioFilename(filename);
        };
        reader.readAsDataURL(blob);
    };

    const handleSave = async () => {
        const sanitizedFormData = {};

        for (const [key, value] of Object.entries(formData)) {
            const cleanKey = toCamelCase(key.trim());

            if (!cleanKey) {
                toast.error("One or more fields are invalid after formatting");
                return;
            }

            if (value !== undefined && value !== null && value.toString().trim() !== "") {
                sanitizedFormData[cleanKey] = value;
            }
        }

        if (!selectedPriority) {
            toast.warn("Please select one of the buttons before saving");
            return;
        }

        sanitizedFormData.priority = selectedPriority;
        if (imagePaths) {
            sanitizedFormData.imagepaths = imagePaths;
        }
        setIsSaving(true);

        try {
            // 1. Save form data to backend
            const response = await fetch(`${backendURL}db/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sanitizedFormData)
            });

            if (!response.ok) {
                toast.error("Error saving data to the server");
                return;
            }

            const insertedId = await response.json();
            setInsertedMongoID(insertedId.inserted_id);

            // 2. Upload audio if present
            if (audioBlob && audioFilename) {
                const audioFormData = new FormData();
                audioFormData.append("audio", audioBlob);
                audioFormData.append("inserted_id", insertedId.inserted_id);

                const audioResponse = await fetch(`${backendURL}audio/upload`, {
                    method: 'POST',
                    body: audioFormData
                });

                if (!audioResponse.ok) {
                    toast.error("Error uploading audio");
                    return;
                }

                const audioResult = await audioResponse.json();
                const audioPath = audioResult.audio_path;
                setAudioPath(audioPath);

                // 3. Transcribe audio
                const transcribeURL = new URL(`${backendURL}audio/transcribe`);
                transcribeURL.searchParams.append('inserted_id', insertedId.inserted_id);

                const transcribeResponse = await fetch(transcribeURL, { method: 'GET' });

                if (!transcribeResponse.ok) {
                    toast.error("Error transcribing audio");
                    return;
                }

                const transcriptResult = await transcribeResponse.json();
                const text = transcriptResult.text;

                toast.success("Data and audio saved, uploaded, and transcribed!");
            } else {
                toast.success("Data saved and uploaded successfully!");
            }

        } catch (error) {
            console.error("Error:", error.message);
            toast.error("An unexpected error occurred");
        }
        finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="bg-[#f1f6f1] bg-cover bg-center min-h-screen">
                <div className="pt-[16vh] md:pt-[14vh] px-6 flex flex-col justify-center max-w-2xl mx-auto rounded-xl shadow-md">
                    <h2 className="playwrite text-green-800 text-xl font-bold mb-8 text-center">Your Card Details</h2>

                    {Object.entries(formData).map(([key, value], idx) => (Object.entries(formData).length !== 0 && value !== undefined && value !== null && value !== '' && (
                        <div key={idx} className="mb-4 border-b pb-3">
                            <div className="flex items-center justify-between">
                                <div className="w-full grid grid-cols-2 items-center gap-4">
                                    <label className="block font-semibold mr-2">{capitalizeFirstLetter(key)}:</label>
                                    <input
                                        type="text"
                                        value={value}
                                        onChange={(e) => handleChange(key, e.target.value)}
                                        disabled={!isEditable}
                                        className="input-edit border rounded px-2 py-1 w-full"
                                    />
                                </div>
                                {isEditable && (
                                    <button
                                        onClick={() => handleDelete(key)}
                                        className="ml-2 text-red-500 hover:text-red-700 font-bold"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>
                    )))}

                    {isEditable && (
                        <div className="mb-6 border-b pb-4">
                            <h3 className="text-blue-600 font-semibold mb-2">Add New Field</h3>
                            <div className="flex gap-2 mb-2 py-2">
                                <input
                                    type="text"
                                    placeholder="Key (required)"
                                    value={newKey}
                                    onChange={(e) => setNewKey(e.target.value)}
                                    className="input-edit border rounded px-2 py-1 w-1/2"
                                />
                                <input
                                    type="text"
                                    placeholder="Value"
                                    value={newValue}
                                    onChange={(e) => setNewValue(e.target.value)}
                                    className="input-edit border rounded px-2 py-1 w-1/2"
                                />
                            </div>
                            <button
                                onClick={handleAddField}
                                className="button-style px-2"
                            >
                                Add Field
                            </button>
                        </div>
                    )}

                    {!isEditable ? (
                        <button
                            onClick={handleEditToggle}
                            className="button-style px-2 mb-4"
                        >
                            Edit
                        </button>
                    ) : (
                        <>
                        </>
                    )}

                    <div className='text-center text-blue-700 font-bold mt-4 mb-2'>Please select any one button below:</div>
                    <div className='flex space-x-8 items-center justify-center'>
                        {PRIORITIES.map((item, index) => {
                            const [key, value] = Object.entries(item)[0];
                            return (
                                <div key={index} className="space-x-1">
                                    <input
                                        type="radio"
                                        name="priority"
                                        id={key}
                                        value={key}
                                        className={value}
                                        checked={selectedPriority === key}
                                        onChange={() => setSelectedPriority(key)}
                                    />
                                    <label htmlFor={`${key}`} className={`${value} font-bold`}>
                                        {key}
                                    </label>
                                </div>
                            );
                        })}
                    </div>

                    <AudioRecorder onRecordingComplete={handleAudioComplete} />

                    <button
                        onClick={handleSave}
                        className={`bg-green-600 text-white font-bold py-1 px-4 rounded mt-2 mb-4 ${(!selectedPriority || isSaving) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={!selectedPriority || isSaving}
                    >
                        {isSaving ? "Saving..." : "Save Data"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default Edit;